import { useState } from "react";
import { Icons } from "@admin/components/icons/Icons";
import { Modal } from "@admin/components/ui/Modal";
import { AdminTable } from "@admin/components/shared/AdminTable";
import { Pagination } from "@admin/components/ui/Pagination";
import { useDebounce, useQuotes } from "@admin/hooks";
import type { Quote } from "@admin/types/quote";
import { OptimizedImage } from "@shared/components";

export function AdminHistorialPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortBy, setSortBy] = useState<"createdAt" | "quoteNumber">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [hasClientDataFilter, setHasClientDataFilter] = useState<
    boolean | undefined
  >(undefined);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Aplicar debounce al searchQuery (500ms de delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const {
    data: quotesResponse,
    isLoading,
    isError,
    error,
  } = useQuotes({
    page: currentPage,
    limit: pageSize,
    sortBy,
    sortOrder,
    search: debouncedSearchQuery || undefined,
    hasClientData: hasClientDataFilter,
  });

  const quotes: Quote[] = quotesResponse?.data ?? [];
  const paginationMeta = quotesResponse?.pagination;
  const totalPages = paginationMeta?.totalPages ?? 1;
  const totalItems = paginationMeta?.total ?? quotes.length;
  const activePage = paginationMeta?.page ?? currentPage;

  // Función para verificar si una cotización está vencida
  const isExpired = (validUntil: string): boolean => {
    return new Date(validUntil) < new Date();
  };

  // Columnas de la tabla
  const columns = [
    {
      header: "NÚMERO",
      accessor: (row: Quote) => (
        <span className="font-medium text-gray-900">{row.quoteNumber}</span>
      ),
    },
    {
      header: "CLIENTE",
      accessor: (row: Quote) => {
        if (!row.client || !row.hasClientData) {
          return (
            <span className="text-gray-400 italic">Sin datos de cliente</span>
          );
        }
        return (
          <div>
            {row.client.name && (
              <p key={row.id + "-client-name"} className="text-sm font-medium text-gray-900">
                {row.client.name}
              </p>
            )}
            {row.client.email && (
              <p key={row.id + "-client-email"} className="text-xs text-gray-500">
                {row.client.email}
              </p>
            )}
            {row.client.phone && (
              <p key={row.id + "-client-phone"} className="text-xs text-gray-500">
                {row.client.phone}
              </p>
            )}
          </div>
        );
      },
    },
    {
      header: "PRODUCTOS",
      accessor: (row: Quote) => (
        <div>
          <p className="text-sm text-gray-900">
            {row.totalItems} {row.totalItems === 1 ? "producto" : "productos"}
          </p>
          <p className="text-xs text-gray-500">
            {row.totalQuantity} unidades
          </p>
        </div>
      ),
    },
    {
      header: "FECHA",
      accessor: (row: Quote) => (
        <div>
          <p className="text-sm text-gray-900">
            {new Date(row.createdAt).toLocaleDateString("es-AR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
          </p>
          <p className="text-xs text-gray-500">
            {new Date(row.createdAt).toLocaleTimeString("es-AR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      ),
    },
    {
      header: "VÁLIDA HASTA",
      accessor: (row: Quote) => {
        const expired = isExpired(row.validUntil);
        return (
          <div>
            <p
              className={`text-sm ${expired ? "text-red-600" : "text-gray-900"}`}
            >
              {new Date(row.validUntil).toLocaleDateString("es-AR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
            </p>
            {expired && (
              <p className="text-xs text-red-500 font-medium">Vencida</p>
            )}
          </div>
        );
      },
    },
  ];

  // Acciones para cada fila
  const renderActions = (quote: Quote) => (
    <div className="flex space-x-2">
      <button
        onClick={() => handleViewDetails(quote)}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Ver detalles"
        key="view-details"
      >
        <Icons.Eye size={16} />
      </button>
      <button
        onClick={() => window.open(quote.pdfUrl, "_blank")}
        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
        title="Ver PDF"
        key="view-pdf"
      >
        <Icons.Eye size={16} />
      </button>
      <button
        onClick={() => {
          const link = document.createElement("a");
          link.href = quote.pdfUrl;
          link.download = `${quote.quoteNumber}.pdf`;
          link.click();
        }}
        className="p-2 text-[#282828] hover:bg-gray-100 rounded-lg transition-colors"
        title="Descargar PDF"
        key="download-pdf"
      >
        <Icons.Edit size={16} />
      </button>
    </div>
  );

  const handleViewDetails = (quote: Quote) => {
    setSelectedQuote(quote);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuote(null);
  };

  return (
    <div className="space-y-6">
      {/* Filtros y controles */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ordenar por
          </label>
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as "createdAt" | "quoteNumber")
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828]"
          >
            <option value="createdAt">Fecha de creación</option>
            <option value="quoteNumber">Número de cotización</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Orden
          </label>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828]"
          >
            <option value="desc">Descendente</option>
            <option value="asc">Ascendente</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cliente
          </label>
          <select
            value={
              hasClientDataFilter === undefined
                ? "all"
                : hasClientDataFilter
                ? "with"
                : "without"
            }
            onChange={(e) => {
              const value = e.target.value;
              setHasClientDataFilter(
                value === "all"
                  ? undefined
                  : value === "with"
                  ? true
                  : false
              );
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828]"
          >
            <option value="all">Todos</option>
            <option value="with">Con datos de cliente</option>
            <option value="without">Sin datos de cliente</option>
          </select>
        </div>
      </div>

      {/* Tabla de cotizaciones */}
      <AdminTable
        title="Historial de Cotizaciones"
        subtitle="Registro de todas las cotizaciones generadas en el sistema"
        searchable={true}
        searchValue={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Buscar por número, cliente o producto..."
        columns={columns}
        data={quotes}
        isLoading={isLoading}
        isError={isError}
        error={error?.message || undefined}
        renderActions={renderActions}
        actionsHeader="Acciones"
        maxHeight="600px"
        pagination={
          <Pagination
            currentPage={activePage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        }
      />

      {/* Modal de detalles */}
      {isModalOpen && selectedQuote && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={`Cotización ${selectedQuote.quoteNumber}`}
          size="lg"
        >
          <div className="space-y-6">
            {/* Información general */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Número de Cotización
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedQuote.quoteNumber}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Fecha de Creación
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedQuote.createdAt).toLocaleString("es-AR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Válida Hasta
                </label>
                <p
                  className={`mt-1 text-sm ${
                    isExpired(selectedQuote.validUntil)
                      ? "text-red-600 font-medium"
                      : "text-gray-900"
                  }`}
                >
                  {new Date(selectedQuote.validUntil).toLocaleString("es-AR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                  {isExpired(selectedQuote.validUntil) && " (Vencida)"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Total de Productos
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedQuote.totalItems} productos •{" "}
                  {selectedQuote.totalQuantity} unidades
                </p>
              </div>
            </div>

            {/* Datos del cliente */}
            {selectedQuote.client && selectedQuote.hasClientData ? (
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Datos del Cliente
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {selectedQuote.client.name && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Nombre
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedQuote.client.name}
                      </p>
                    </div>
                  )}
                  {selectedQuote.client.email && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedQuote.client.email}
                      </p>
                    </div>
                  )}
                  {selectedQuote.client.phone && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Teléfono
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedQuote.client.phone}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 italic">
                  No se proporcionaron datos de cliente
                </p>
              </div>
            )}

            {/* Productos cotizados */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Productos Cotizados
              </h3>
              <div className="space-y-4">
                {selectedQuote.items.map((item) => (
                  <div
                    key={item.productId}
                    className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg"
                  >
                    {item.imageUrl && (
                      <div className="flex-shrink-0">
                        <OptimizedImage
                          src={item.imageUrl}
                          alt={item.productName}
                          className="w-16 h-16 object-cover rounded"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {item.productName}
                      </p>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-1">
                          {item.description}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="text-xs text-gray-600">
                          Cantidad: {item.quantity}
                        </span>
                        {item.unitOfMeasurement && (
                          <span className="text-xs text-gray-600">
                            Unidad: {item.unitOfMeasurement}
                          </span>
                        )}
                        {item.brand && (
                          <span className="text-xs text-gray-600">
                            Marca: {item.brand}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Acciones */}
            <div className="border-t pt-4 flex justify-end space-x-3">
              <button
                onClick={() => window.open(selectedQuote.pdfUrl, "_blank")}
                className="px-4 py-2 text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
              >
                Ver PDF
              </button>
              <button
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = selectedQuote.pdfUrl;
                  link.download = `${selectedQuote.quoteNumber}.pdf`;
                  link.click();
                }}
                className="px-4 py-2 text-[#282828] bg-white border border-[#282828] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors"
              >
                Descargar PDF
              </button>
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
